import React from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { legalApi } from "@/services/api";
import { useAsync } from "@/hooks/useAsync";
import { LoadingState, ErrorState } from "@/components/DataStates";

function Block({ block }) {
  if (block.type === "ul") {
    return (
      <ul className="mt-2 list-disc space-y-1.5 pl-5 text-sm leading-relaxed text-slate-600">
        {block.items.map((it, i) => <li key={i}>{it}</li>)}
      </ul>
    );
  }
  return <p className="mt-2 text-sm leading-relaxed text-slate-600">{block.text}</p>;
}

export default function LegalDoc() {
  const { type } = useParams();
  const { data, loading, error, reload } = useAsync(() => legalApi.document(type), [type]);

  const payload = data?.data || data;
  const doc = payload?.document;
  const controller = payload?.controller;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-10 border-b border-slate-200 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-4">
          <Link to="/" className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800" data-testid="legal-back">
            <ArrowLeft className="h-4 w-4" /> Back
          </Link>
          <span className="ml-auto flex items-center gap-1.5 text-sm font-semibold text-primary">
            <ShieldCheck className="h-4 w-4" /> FD Check
          </span>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-8" data-testid="legal-doc">
        {loading && <LoadingState label="Loading…" />}
        {error && <ErrorState onRetry={reload} />}
        {doc && (
          <article className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-10">
            <h1 className="text-3xl font-bold tracking-tight text-slate-900" data-testid="legal-doc-title">{doc.title}</h1>
            <p className="mt-1 text-xs font-medium uppercase tracking-wide text-slate-400">
              Version {doc.version} · Effective {doc.effective_date}
            </p>
            {doc.intro && <p className="mt-5 text-sm leading-relaxed text-slate-700">{doc.intro}</p>}

            <div className="mt-6 space-y-7">
              {doc.sections.map((s, i) => (
                <section key={i}>
                  <h2 className="text-lg font-semibold text-slate-900">{s.heading}</h2>
                  {s.blocks.map((b, j) => <Block key={j} block={b} />)}
                </section>
              ))}
            </div>

            {controller && (
              <div className="mt-8 border-t border-slate-100 pt-5 text-xs text-slate-400">
                {controller.name} · Company no. {controller.company_number} · {controller.address}
              </div>
            )}
          </article>
        )}
      </main>
    </div>
  );
}
